/* eslint-disable */
import React, { FC, useContext, useEffect, useState } from "react";
import { amplitudeEventLogger } from "@collectivehealth/frontend-analytics";

// Components
import "@collectivehealth/dls-link";
import "@collectivehealth/dls-logos/dist/es/elements/dls-logo-wordmark-horizontal";
import MenuBar from "components/templates/MenuBar";
import usePageEnteredLogger from "hooks/usePageEnteredLogger";

// Context
import PageTitleContext from "contexts/PageTitle";
import UrlSponsorContext from "contexts/UrlSponsor";
import SponsorAdminContext from "contexts/SponsorAdmin";

// Services
import AssetService, { Asset, Response } from "./Asset.service";

// Constants
import { ROLES } from "constants/authorization";

// Stylings, Assets
import { ReactComponent as LoaderSvg } from "components/loaders/OverlayLoader/loader.svg";
import SpringbukLogo from "./SpringbukBlackLogo.png";
import styles from "./SpringbukAnalytics.css";

const COMPASS_COLLECTION_CODE = "compass-portal-01";
const SPRINGBUK_ASSET_KEY = "springbuk-logo-wordmark-horizontal-01";
const SPRINGBUK_ANALYTIC_LINK = "https://collectivehealth.springbuk.com";
const PAGE_TITLE = "Springbuk Analytics";

const SpringbukAnalytics: FC = () => {
  usePageEnteredLogger(PAGE_TITLE);

  const [, setPageTitle] = useContext(PageTitleContext);
  useEffect(() => {
    setPageTitle(PAGE_TITLE);
  }, [setPageTitle]);

  const [springbukLogoSrc, setSpringbukLogoSrc] = useState(SpringbukLogo);
  const [isWaiting, setIsWaiting] = useState(true);
  const urlSponsor: any = useContext(UrlSponsorContext);
  const sponsorAdmin = useContext(SponsorAdminContext);

  // auto launch springbuk page on a new tab after 5 seconds
  useEffect((): (() => void) => {
    const timerID: number = window.setTimeout((): void => {
      logAmplitudeEvent();
      window.open(SPRINGBUK_ANALYTIC_LINK, "_blank");
      setIsWaiting(false);
    }, 5000);

    return (): void => {
      clearTimeout(timerID);
    };
  }, []);

  useEffect(() => {
    try {
      const assetResponse: Promise<Response> = AssetService.getAssetsForCollection(
        COMPASS_COLLECTION_CODE
      );
      assetResponse.then((response: Response) => {
        const assetUrl: string = response.data.filter(
          (asset: Asset) => asset.key === SPRINGBUK_ASSET_KEY
        )[0].value.url;
        setSpringbukLogoSrc(prevState => (assetUrl ? assetUrl : prevState));
      });
    } catch (e) {
      // use default logo if fetching assets run into any problem
      setSpringbukLogoSrc(SpringbukLogo);
    }
  }, []);

  function logAmplitudeEvent(isUserAction?: boolean): void {
    amplitudeEventLogger("compassSpringbukLaunched", {
      isUserAction: !!isUserAction,
      sponsorId: urlSponsor.id,
      sponsorAdminId: sponsorAdmin.id,
      role: (() =>
        Object.entries(ROLES).filter(
          ([, value]) => value === sponsorAdmin.sponsorGroupType
        )[0])()[0] // [ FULL, benefitTeam ]
    });
  }

  return (
    <React.Fragment>
      <MenuBar />
      <div className={styles.pageContent}>
        <div className={styles.chLogo}>{isWaiting ? <LoaderSvg /> : <dls-logo-brandmark />}</div>
        <div className={styles.brandName}>
          <dls-logo-wordmark-horizontal />
          <dls-icon-arrow-right />
          <img alt="springbuk logo" src={springbukLogoSrc} />
        </div>
        <div className={styles.redirectionMessage}>
          You're being redirected to our analytics platform powered by Springbuk.
          <br />
          If you're not being automatically redirected in 5 seconds,{" "}
          <dls-link
            onClick={(): void => logAmplitudeEvent(true)}
            href={SPRINGBUK_ANALYTIC_LINK}
            rel="noopener"
            target="_blank"
          >
            click here
          </dls-link>
          .
        </div>
      </div>
    </React.Fragment>
  );
};

export default SpringbukAnalytics;
